import * as React from 'react';
import { LocalizeContextProps, withLocalize } from 'react-localize-redux';
import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router';
import Select, { ValueType } from 'react-select';
import { Button, Form, Input } from 'reactstrap';
import { Dispatch } from 'redux';
import { Application, Privilege } from '../../clients/aasServerClient.generated';
import { clearSingleApplication, fetchSingleApplication, saveSingleApplication } from '../../stores/applications/actionCreators';
import { getSingleApplication } from '../../stores/applications/selectors';
import { isLoading } from '../../stores/navigation/selectors';
import { RootState } from '../../stores/store';
import SideModal from '../common/modals/SideModal';
import { LoadingButton } from '../navigation/LoadingButton';
import LoadingScreen from '../navigation/LoadingScreen';
import ServerSelect from '../servers/ServerSelect';
import WorkspaceSelect from '../workspaces/WorkspaceSelect';
import ApplicationProperties from './ApplicationProperties';

interface Props extends LocalizeContextProps, RouteComponentProps<{ id: string }> {
    isLoading: boolean;
    application: {
        loaded: boolean;
        saved: boolean;
        application?: Partial<Application>;
    };

    server: Array<{ label: string, value: string }>;
    fetchSingleApplication: (id: string) => void;
    clearSingleApplication: () => void;
    saveSingleApplication: (
        application: Partial<Application>,
        workspaceId?: string,
        server?: string,
    ) => void;
}

interface State {
    application?: Partial<Application>;
    serverSelection?: { label: string, value: string };
    workspaceSelection?: { label: string, value: string };
}

class EditApplicationPage extends React.Component<Props, State> {

    public constructor(props: Props) {
        super(props);
        this.state = {
            application: undefined,
        };
        const id = this.props.match.params.id;
        this.props.clearSingleApplication();
        this.props.fetchSingleApplication(id);
    }

    public componentDidUpdate(prevProps: Props) {
        if (!prevProps.application.loaded && this.props.application.loaded) {
            this.setState({
                application: this.props.application.application,
            });
        }
        if (!prevProps.application.saved && this.props.application.saved) {
            this.closePage();
        }
    }
    public modalRef = React.createRef<HTMLDivElement>();

    public render() {
        const t = this.props.translate;
        return (
            <SideModal
                isOpen={true}
                toggle={this.closePage}
                headerText={t('applications.edit.header.edit') as string}
                footer={this.renderFooter()}
                objectRef={this.modalRef}
            >
                {this.props.application.loaded && this.props.application.application ? (
                    <Form>
                        <h3 className="mb-4 mt-2">{t('applications.edit.section.details')}</h3>
                        <ApplicationProperties
                            type="edit"
                            application={this.props.application.application}
                            updateApplication={this.updateApplication}
                        />
                        {this.props.application.application!.server ? (
                            <React.Fragment>
                                <hr className="fill-modal" />
                                <h3 className="mb-4 mt-4">{t('applications.edit.section.server')}</h3>
                                <ServerSelect
                                    type="single"
                                    selected={this.props.application.application.server.hostname}
                                    updateSelection={this.updateServerSelection}
                                    selection={this.state.serverSelection}
                                />
                            </React.Fragment>
                        ) : null}
                        {this.props.application.application!.workspace ? (
                            <React.Fragment>
                                <hr className="fill-modal" />
                                <h3 className="mb-4 mt-4">{t('applications.edit.section.workspace')}</h3>
                                <WorkspaceSelect
                                    isMulti={false}
                                    selected={this.props.application.application.workspace.id}
                                    updateSelection={this.updateWorkspaceSelection}
                                    selection={this.state.workspaceSelection}
                                />
                            </React.Fragment>
                        ) : null}
                    </Form>
                ) : (
                        <LoadingScreen text={t('loading.loading') as string} />
                    )}
            </SideModal>
        );
    }

    private updateApplication = (application: Partial<Application>) => {
        this.setState({
            application,
        });
    }

    private updateServerSelection = (selection: ValueType<{ label: string, value: string }> |
    { label: string, value: string }) => {
        this.setState({ serverSelection: selection as { label: string, value: string } });
    }

    private updateWorkspaceSelection = (selection: ValueType<{ label: string, value: string }> |
    { label: string, value: string }) => {
        this.setState({ workspaceSelection: selection as { label: string, value: string } });
    }

    private closePage = () => {
        this.props.history.push('/applications');
    }

    private renderFooter = (): React.ReactNode => {
        const t = this.props.translate;
        return (
            <React.Fragment>
                <LoadingButton
                    loading={this.props.isLoading}
                    color="success"
                    onClick={this.saveApplication}
                    style={{ width: '200px' }}
                >
                    {t('applications.edit.button.save')}
                </LoadingButton>
            </React.Fragment>
        );
    }

    private saveApplication = () => {
        const application = this.state.application;
        const workspaceId = this.state.workspaceSelection?.value;
        const server = this.state.serverSelection?.value;
        this.props.saveSingleApplication(application!, workspaceId, server);
    }
}

const mapStateToProps = (state: RootState) => ({
    application: getSingleApplication(state),
    isLoading: isLoading(state),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    clearSingleApplication: () => dispatch(clearSingleApplication()),
    fetchSingleApplication: (id: string) => dispatch(fetchSingleApplication(id)),
    saveSingleApplication: (
        application: Partial<Application>,
        workspaceId?: string,
        server?: string,
    ) => dispatch(saveSingleApplication(application, server, workspaceId))
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(withLocalize(EditApplicationPage)));